import { createElement, Component, useContext, useRef, useEffect, createContext, useState, Fragment } from 'react';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var YMAPS_LOADING_STATE;
(function (YMAPS_LOADING_STATE) {
    YMAPS_LOADING_STATE[YMAPS_LOADING_STATE["NOT_LOADED"] = 0] = "NOT_LOADED";
    YMAPS_LOADING_STATE[YMAPS_LOADING_STATE["LOADING"] = 1] = "LOADING";
    YMAPS_LOADING_STATE[YMAPS_LOADING_STATE["LOADED"] = 2] = "LOADED";
})(YMAPS_LOADING_STATE || (YMAPS_LOADING_STATE = {}));
var ymapsLoadingState = YMAPS_LOADING_STATE.NOT_LOADED;
var ymapsPromise = null;
function getYmapsPromise() {
    if (!ymapsPromise) {
        ymapsPromise = new Promise(function (resolve, reject) {
            if ('ymaps' in window) {
                ymaps.ready(resolve);
                return;
            }
            var ymapsScript = document.getElementById('ymaps-loader');
            if (ymapsScript) {
                ymapsScript.addEventListener('load', function () {
                    ymaps.ready(resolve);
                });
            }
            else {
                reject(new Error('Can\'t find either "window.ymaps" or <script id="ymaps-loader"/>'));
            }
        });
    }
    return ymapsPromise;
}
function withYmaps(BaseComponent) {
    return /** @class */ (function (_super) {
        __extends(WithYmaps, _super);
        function WithYmaps() {
            var _this = _super !== null && _super.apply(this, arguments) || this;
            _this.unmounted = false;
            return _this;
        }
        WithYmaps.prototype.render = function () {
            return ymapsLoadingState === YMAPS_LOADING_STATE.LOADED &&
                createElement(BaseComponent, __assign({}, this.props));
        };
        WithYmaps.prototype.componentDidMount = function () {
            var _this = this;
            switch (ymapsLoadingState) {
                case YMAPS_LOADING_STATE.NOT_LOADED:
                    ymapsLoadingState = YMAPS_LOADING_STATE.LOADING;
                    getYmapsPromise().then(function () {
                        ymapsLoadingState = YMAPS_LOADING_STATE.LOADED;
                        if (!_this.unmounted) {
                            _this.forceUpdate();
                        }
                    });
                    break;
                case YMAPS_LOADING_STATE.LOADING:
                    getYmapsPromise().then(function () {
                        if (!_this.unmounted) {
                            _this.forceUpdate();
                        }
                    });
                    break;
            }
        };
        WithYmaps.prototype.componentWillUnmount = function () {
            this.unmounted = true;
        };
        return WithYmaps;
    }(Component));
}

function useGeoObjectCollection() {
    return useContext(GeoObjectCollectionContext);
}

function useGeoObject(factory) {
    var geoObjectCollection = useGeoObjectCollection();
    var geoObjectRef = useRef(null);
    if (geoObjectRef.current === null) {
        geoObjectRef.current = factory();
        geoObjectCollection.add(geoObjectRef.current);
    }
    var geoObject = geoObjectRef.current;
    useEffect(function () { return function () {
        geoObjectCollection.remove(geoObject);
    }; }, []);
    return geoObject;
}

function usePrev(current) {
    var ref = useRef(null);
    var res = ref.current;
    ref.current = current;
    return res;
}

function handleEventListener(obj, event, nextEventHandler, prevEventHandler) {
    if (nextEventHandler !== prevEventHandler) {
        if (prevEventHandler) {
            obj.events.remove(event, prevEventHandler);
        }
        if (nextEventHandler) {
            obj.events.add(event, nextEventHandler);
        }
    }
}
function handleData(dataManager, nextData, prevData) {
    if (nextData === prevData) {
        return;
    }
    if (prevData) {
        for (var prevKey in prevData) {
            if (!nextData || !(prevKey in nextData)) {
                if (!dataManager.isFrozen()) {
                    dataManager.freeze();
                }
                dataManager.unset(prevKey);
            }
            else if (nextData[prevKey] !== prevData[prevKey]) {
                if (!dataManager.isFrozen()) {
                    dataManager.freeze();
                }
                dataManager.set(prevKey, nextData[prevKey]);
            }
        }
    }
    if (nextData) {
        for (var nextKey in nextData) {
            if (!prevData || !(nextKey in prevData)) {
                if (!dataManager.isFrozen()) {
                    dataManager.freeze();
                }
                dataManager.set(nextKey, nextData[nextKey]);
            }
        }
    }
    if (dataManager.isFrozen()) {
        dataManager.unfreeze();
    }
}

var GeoObjectCollectionContext = createContext(null);
var GeoObjectCollection = function (props) {
    var geoObjectCollection = useGeoObject(function () { return new ymaps.GeoObjectCollection(undefined, props.options); });
    var prevProps = usePrev(props);
    handleEventListener(geoObjectCollection, 'click', props.onClick, prevProps && prevProps.onClick);
    handleEventListener(geoObjectCollection, 'mouseenter', props.onMouseEnter, prevProps && prevProps.onMouseEnter);
    handleEventListener(geoObjectCollection, 'mouseleave', props.onMouseLeave, prevProps && prevProps.onMouseLeave);
    if (prevProps) {
        handleData(geoObjectCollection.options, props.options, prevProps.options);
    }
    return (createElement(GeoObjectCollectionContext.Provider, { value: geoObjectCollection }, props.children));
};

function arePointCoordinatesEqual(coords1, coords2) {
    return coords1 === coords2 || (coords1[0] === coords2[0] && coords1[1] === coords2[1]);
}
function areBoundsEqual(bounds1, bounds2) {
    return bounds1 === bounds2 || (bounds1[0][0] === bounds2[0][0] &&
        bounds1[0][1] === bounds2[0][1] &&
        bounds1[1][0] === bounds2[1][0] &&
        bounds1[1][1] === bounds2[1][1]);
}

var MapContext = createContext(null);
var Map = function (props) {
    var _a = useState(null), map = _a[0], setMap = _a[1];
    var elemRef = useRef(null);
    var cursorAccessorRef = useRef(null);
    var prevProps = usePrev(props);
    useEffect(function () {
        var map = new ymaps.Map(elemRef.current, __assign({ type: props.type, controls: [], behaviors: props.behaviors }, props.center ?
            { center: props.center, zoom: props.zoom } :
            { bounds: props.bounds }), props.options);
        if (props.margin) {
            map.margin.setDefaultMargin(props.margin);
        }
        if (props.cursor) {
            cursorAccessorRef.current = map.cursors.push(props.cursor);
        }
        map.layers.events.add('click', function (e) {
            if (e.get('target') instanceof ymaps.hotspot.Layer) {
                map.events.fire(e.get('type'), e);
            }
        });
        handleEventListener(map, 'init', props.onInit);
        handleEventListener(map, 'typechange', props.onTypeChange);
        handleEventListener(map, 'boundschange', props.onBoundsChange);
        handleEventListener(map.zoomRange, 'change', props.onZoomRangeChange);
        setMap(map);
        map.events.fire('init', { map: map, zoom: map.getZoom(), center: map.getCenter(), bounds: map.getBounds() });
        return function () {
            map.destroy();
        };
    }, []);
    if (map && prevProps) {
        handleEventListener(map, 'typechange', props.onTypeChange, prevProps.onTypeChange);
        handleEventListener(map, 'boundschange', props.onBoundsChange, prevProps.onBoundsChange);
        handleEventListener(map.zoomRange, 'change', props.onZoomRangeChange, prevProps.onZoomRangeChange);
        if (props.margin !== prevProps.margin) {
            map.margin.setDefaultMargin(props.margin || 0);
        }
        if (props.cursor !== prevProps.cursor) {
            if (props.cursor) {
                if (cursorAccessorRef.current === null) {
                    cursorAccessorRef.current = map.cursors.push(props.cursor);
                }
                else {
                    cursorAccessorRef.current.setKey(props.cursor);
                }
            }
            else if (cursorAccessorRef.current !== null) {
                cursorAccessorRef.current.remove();
                cursorAccessorRef.current = null;
            }
        }
        handleData(map.options, props.options, prevProps.options);
        if (props.type && props.type !== prevProps.type && props.type !== map.getType()) {
            map.setType(props.type);
        }
        if (props.center) {
            if ((!prevProps.center ||
                !arePointCoordinatesEqual(props.center, prevProps.center) ||
                props.zoom !== prevProps.zoom) &&
                (!arePointCoordinatesEqual(props.center, map.getCenter()) ||
                    map.getZoom() !== props.zoom)) {
                map.setCenter(props.center, props.zoom, props.setCenterOptions);
            }
        }
        else if ((!prevProps.bounds || !areBoundsEqual(props.bounds, prevProps.bounds)) &&
            !areBoundsEqual(map.getBounds(), props.bounds)) {
            map.setBounds(props.bounds, props.setBoundsOptions);
        }
    }
    return (createElement(Fragment, null,
        createElement("div", { className: props.className, ref: elemRef }),
        map &&
            createElement(MapContext.Provider, { value: map },
                createElement(GeoObjectCollectionContext.Provider, { value: map.geoObjects }, props.children))));
};
var _Map = withYmaps(Map);

function useMap() {
    return useContext(MapContext);
}

var MapEventListener = function (props) {
    var map = useMap();
    useEffect(function () {
        map.events.add(props.type, props.callback);
        return function () {
            map.events.remove(props.type, props.callback);
        };
    }, [props.type, props.callback]);
    return null;
};

function useLayer(factory) {
    var map = useMap();
    var layerRef = useRef(null);
    if (layerRef.current === null) {
        layerRef.current = factory();
        map.layers.add(layerRef.current);
    }
    var layer = layerRef.current;
    useEffect(function () { return function () {
        map.layers.remove(layer);
    }; }, []);
    return layer;
}

var Layer = function (props) {
    var layer = useLayer(function () { return new ymaps.Layer(props.tileUrlTemplate, props.options); });
    var prevProps = usePrev(props);
    if (prevProps) {
        handleData(layer.options, props.options, prevProps.options);
        if (props.tileUrlTemplate !== prevProps.tileUrlTemplate) {
            layer.setTileUrlTemplate(props.tileUrlTemplate);
            layer.update();
        }
    }
    return null;
};

var HotspotLayer = function (props) {
    var hotspotLayer = useLayer(function () { return new ymaps.hotspot.Layer(new ymaps.hotspot.ObjectSource(props.objectSource.tileUrlTemplate, props.objectSource.keyTemplate, props.objectSource.options), props.options); });
    var prevProps = usePrev(props);
    handleEventListener(hotspotLayer, 'click', props.onClick, prevProps && prevProps.onClick);
    handleEventListener(hotspotLayer, 'dblclick', props.onDblClick, prevProps && prevProps.onDblClick);
    handleEventListener(hotspotLayer, 'mouseenter', props.onMouseEnter, prevProps && prevProps.onMouseEnter);
    handleEventListener(hotspotLayer, 'mouseleave', props.onMouseLeave, prevProps && prevProps.onMouseLeave);
    handleEventListener(hotspotLayer, 'contextmenu', props.onContextMenu, prevProps && prevProps.onContextMenu);
    if (prevProps) {
        var ymapsObjectSource = hotspotLayer.getObjectSource();
        handleData(ymapsObjectSource.options, props.objectSource.options, prevProps.objectSource.options);
        handleData(hotspotLayer.options, props.options, prevProps.options);
        var shouldUpdateLayer = false;
        if (props.objectSource.tileUrlTemplate !== prevProps.objectSource.tileUrlTemplate) {
            ymapsObjectSource.setTileUrlTemplate(props.objectSource.tileUrlTemplate);
            shouldUpdateLayer = true;
        }
        if (props.objectSource.keyTemplate !== prevProps.objectSource.keyTemplate) {
            // `objectSource.keyTemplate` can be undefined and `!` is a hack for ymaps api
            ymapsObjectSource.setKeyTemplate(props.objectSource.keyTemplate);
            shouldUpdateLayer = true;
        }
        if (shouldUpdateLayer) {
            hotspotLayer.update();
        }
    }
    return null;
};

var Placemark = function (props) {
    var placemark = useGeoObject(function () { return new ymaps.Placemark(props.geometry, props.properties, props.options); });
    var prevProps = usePrev(props);
    if (prevProps) {
        if (placemark.geometry.getCoordinates() === props.geometry.coordinates) {
            handleEventListener(placemark, 'geometrychange', props.onGeometryChange, prevProps.onGeometryChange);
        }
        else {
            handleEventListener(placemark, 'geometrychange', null, prevProps.onGeometryChange);
            placemark.geometry.setCoordinates(props.geometry.coordinates);
            handleEventListener(placemark, 'geometrychange', props.onGeometryChange);
        }
    }
    else {
        handleEventListener(placemark, 'geometrychange', props.onGeometryChange);
    }
    handleEventListener(placemark, 'click', props.onClick, prevProps && prevProps.onClick);
    handleEventListener(placemark, 'dblclick', props.onDblClick, prevProps && prevProps.onDblClick);
    handleEventListener(placemark, 'dragend', props.onDragEnd, prevProps && prevProps.onDragEnd);
    handleEventListener(placemark, 'editorstatechange', props.onEditorStateChange, prevProps && prevProps.onEditorStateChange);
    if (prevProps) {
        handleData(placemark.properties, props.properties, prevProps.properties);
        handleData(placemark.options, props.options, prevProps.options);
    }
    handleData(placemark.editor.options, props.editingOptions, prevProps && prevProps.editingOptions);
    if (!!props.editing !== !!(prevProps && prevProps.editing)) {
        if (props.editing) {
            if (isGeometryEmpty(props.geometry)) {
                placemark.editor.startDrawing();
            }
            else {
                placemark.editor.startEditing();
            }
        }
        else {
            placemark.editor.stopEditing();
        }
    }
    else if (props.editing && !placemark.editor.state.get('drawing') && isGeometryEmpty(props.geometry)) {
        placemark.editor.startDrawing();
    }
    return null;
};
function isGeometryEmpty(geometry) {
    return geometry.coordinates === null || geometry.coordinates.length === 0;
}

var Polyline = function (props) {
    var polyline = useGeoObject(function () { return new ymaps.Polyline(props.geometry, props.properties, props.options); });
    var prevProps = usePrev(props);
    if (prevProps) {
        if (polyline.geometry.getCoordinates() === props.geometry.coordinates) {
            handleEventListener(polyline, 'geometrychange', props.onGeometryChange, prevProps.onGeometryChange);
        }
        else {
            handleEventListener(polyline, 'geometrychange', null, prevProps.onGeometryChange);
            polyline.geometry.setCoordinates(props.geometry.coordinates);
            handleEventListener(polyline, 'geometrychange', props.onGeometryChange);
        }
    }
    else {
        handleEventListener(polyline, 'geometrychange', props.onGeometryChange);
    }
    handleEventListener(polyline, 'click', props.onClick, prevProps && prevProps.onClick);
    handleEventListener(polyline, 'mouseenter', props.onMouseEnter, prevProps && prevProps.onMouseEnter);
    handleEventListener(polyline, 'mouseleave', props.onMouseLeave, prevProps && prevProps.onMouseLeave);
    handleEventListener(polyline, 'dblclick', props.onDblClick, prevProps && prevProps.onDblClick);
    handleEventListener(polyline, 'dragend', props.onDragEnd, prevProps && prevProps.onDragEnd);
    handleEventListener(polyline, 'editorstatechange', props.onEditorStateChange, prevProps && prevProps.onEditorStateChange);
    if (prevProps) {
        handleData(polyline.properties, props.properties, prevProps.properties);
        handleData(polyline.options, props.options, prevProps.options);
    }
    handleData(polyline.editor.options, props.editingOptions, prevProps && prevProps.editingOptions);
    if (!!props.editing !== !!(prevProps && prevProps.editing)) {
        if (props.editing) {
            if (isGeometryEmpty$1(props.geometry)) {
                polyline.editor.startDrawing();
            }
            else {
                polyline.editor.startEditing();
            }
        }
        else {
            polyline.editor.stopEditing();
        }
    }
    else if (props.editing && !polyline.editor.state.get('drawing') && isGeometryEmpty$1(props.geometry)) {
        polyline.editor.startDrawing();
    }
    return null;
};
function isGeometryEmpty$1(geometry) {
    return geometry.coordinates.length === 0;
}

var Polygon = function (props) {
    var polygon = useGeoObject(function () { return new ymaps.Polygon(props.geometry, props.properties, props.options); });
    var prevProps = usePrev(props);
    if (prevProps) {
        if (polygon.geometry.getCoordinates() === props.geometry.coordinates) {
            handleEventListener(polygon, 'geometrychange', props.onGeometryChange, prevProps.onGeometryChange);
        }
        else {
            handleEventListener(polygon, 'geometrychange', null, prevProps.onGeometryChange);
            polygon.geometry.setCoordinates(props.geometry.coordinates);
            handleEventListener(polygon, 'geometrychange', props.onGeometryChange);
        }
    }
    else {
        handleEventListener(polygon, 'geometrychange', props.onGeometryChange);
    }
    handleEventListener(polygon, 'click', props.onClick, prevProps && prevProps.onClick);
    handleEventListener(polygon, 'dblclick', props.onDblClick, prevProps && prevProps.onDblClick);
    handleEventListener(polygon, 'mouseenter', props.onMouseEnter, prevProps && prevProps.onMouseEnter);
    handleEventListener(polygon, 'mouseleave', props.onMouseLeave, prevProps && prevProps.onMouseLeave);
    handleEventListener(polygon, 'dragend', props.onDragEnd, prevProps && prevProps.onDragEnd);
    handleEventListener(polygon, 'editorstatechange', props.onEditorStateChange, prevProps && prevProps.onEditorStateChange);
    if (prevProps) {
        handleData(polygon.properties, props.properties, prevProps.properties);
        handleData(polygon.options, props.options, prevProps.options);
    }
    handleData(polygon.editor.options, props.editingOptions, prevProps && prevProps.editingOptions);
    if (!!props.editing !== !!(prevProps && prevProps.editing)) {
        if (props.editing) {
            if (isGeometryEmpty$2(props.geometry)) {
                polygon.editor.startDrawing();
            }
            else {
                polygon.editor.startEditing();
            }
        }
        else {
            polygon.editor.stopEditing();
        }
    }
    else if (props.editing && !polygon.editor.state.get('drawing') && isGeometryEmpty$2(props.geometry)) {
        polygon.editor.startDrawing();
    }
    return null;
};
function isGeometryEmpty$2(geometry) {
    return geometry.coordinates.length === 0 || geometry.coordinates[0].length === 0;
}

var Rectangle = function (props) {
    var rectangle = useGeoObject(function () { return new ymaps.Rectangle(props.geometry, props.properties, props.options); });
    var prevProps = usePrev(props);
    if (prevProps) {
        if (rectangle.geometry.getCoordinates() === props.geometry.coordinates) {
            handleEventListener(rectangle, 'geometrychange', props.onGeometryChange, prevProps.onGeometryChange);
        }
        else {
            handleEventListener(rectangle, 'geometrychange', null, prevProps.onGeometryChange);
            rectangle.geometry.setCoordinates(props.geometry.coordinates);
            handleEventListener(rectangle, 'geometrychange', props.onGeometryChange);
        }
    }
    else {
        handleEventListener(rectangle, 'geometrychange', props.onGeometryChange);
    }
    handleEventListener(rectangle, 'click', props.onClick, prevProps && prevProps.onClick);
    handleEventListener(rectangle, 'dblclick', props.onDblClick, prevProps && prevProps.onDblClick);
    handleEventListener(rectangle, 'dragend', props.onDragEnd, prevProps && prevProps.onDragEnd);
    if (prevProps) {
        handleData(rectangle.properties, props.properties, prevProps.properties);
        handleData(rectangle.options, props.options, prevProps.options);
    }
    return null;
};

var Circle = function (props) {
    var circle = useGeoObject(function () { return new ymaps.Circle(props.geometry, props.properties, props.options); });
    var prevProps = usePrev(props);
    if (prevProps) {
        if (circle.geometry.getCoordinates() === props.geometry.coordinates &&
            circle.geometry.getRadius() === props.geometry.radius) {
            handleEventListener(circle, 'geometrychange', props.onGeometryChange, prevProps.onGeometryChange);
        }
        else {
            handleEventListener(circle, 'geometrychange', null, prevProps.onGeometryChange);
            circle.geometry.setCoordinates(props.geometry.coordinates);
            circle.geometry.setRadius(props.geometry.radius);
            handleEventListener(circle, 'geometrychange', props.onGeometryChange);
        }
    }
    else {
        handleEventListener(circle, 'geometrychange', props.onGeometryChange);
    }
    handleEventListener(circle, 'click', props.onClick, prevProps && prevProps.onClick);
    handleEventListener(circle, 'dblclick', props.onDblClick, prevProps && prevProps.onDblClick);
    handleEventListener(circle, 'dragend', props.onDragEnd, prevProps && prevProps.onDragEnd);
    handleEventListener(circle, 'editorstatechange', props.onEditorStateChange, prevProps && prevProps.onEditorStateChange);
    if (prevProps) {
        handleData(circle.properties, props.properties, prevProps.properties);
        handleData(circle.options, props.options, prevProps.options);
    }
    handleData(circle.editor.options, props.editingOptions, prevProps && prevProps.editingOptions);
    if (!!props.editing !== !!(prevProps && prevProps.editing)) {
        if (props.editing) {
            if (isGeometryEmpty$3(props.geometry)) {
                circle.editor.startDrawing();
            }
            else {
                circle.editor.startEditing();
            }
        }
        else {
            circle.editor.stopEditing();
        }
    }
    else if (props.editing && !circle.editor.state.get('drawing') && isGeometryEmpty$3(props.geometry)) {
        circle.editor.startDrawing();
    }
    return null;
};
function isGeometryEmpty$3(geometry) {
    return geometry.coordinates === null || geometry.coordinates.length === 0;
}

var Clusterer = function (props) {
    var clusterer = useGeoObject(function () { return new ymaps.Clusterer(props.options); });
    var prevProps = usePrev(props);
    if (prevProps) {
        handleData(clusterer.options, props.options, prevProps.options);
    }
    return (createElement(GeoObjectCollectionContext.Provider, { value: clusterer }, props.children));
};

function useControl(factory) {
    var map = useMap();
    var controlRef = useRef(null);
    if (controlRef.current === null) {
        controlRef.current = factory();
        map.controls.add(controlRef.current);
    }
    var control = controlRef.current;
    useEffect(function () { return function () {
        map.controls.remove(control);
    }; }, []);
    return control;
}

var Button = function (props) {
    var control = useControl(function () { return new ymaps.control.Button({
        data: props.data,
        options: props.options,
        state: props.state
    }); });
    var prevProps = usePrev(props);
    var onClick = !props.state || props.state.enabled !== false ?
        props.onClick :
        undefined;
    var prevOnClick = prevProps && (!prevProps.state || prevProps.state.enabled !== false) ?
        prevProps.onClick :
        undefined;
    handleEventListener(control, 'click', onClick, prevOnClick);
    handleEventListener(control, 'select', props.onSelect, prevProps && prevProps.onSelect);
    handleEventListener(control, 'deselect', props.onDeselect, prevProps && prevProps.onDeselect);
    if (prevProps) {
        handleData(control.data, props.data, prevProps.data);
        handleData(control.options, props.options, prevProps.options);
        handleData(control.state, props.state, prevProps.state);
    }
    return null;
};

var RulerControl = function (props) {
    var control = useControl(function () { return new ymaps.control.RulerControl({
        data: props.data,
        options: props.options,
        state: props.state
    }); });
    var prevProps = usePrev(props);
    handleEventListener(control, 'select', props.onSelect, prevProps && prevProps.onSelect);
    handleEventListener(control, 'deselect', props.onDeselect, prevProps && prevProps.onDeselect);
    if (prevProps) {
        handleData(control.data, props.data, prevProps.data);
        handleData(control.options, props.options, prevProps.options);
        handleData(control.state, props.state, prevProps.state);
    }
    return null;
};

var SearchControl = function (props) {
    var control = useControl(function () { return new ymaps.control.SearchControl({
        data: props.data,
        options: props.options,
        state: props.state
    }); });
    var prevProps = usePrev(props);
    if (prevProps) {
        handleData(control.data, props.data, prevProps.data);
        handleData(control.options, props.options, prevProps.options);
        handleData(control.state, props.state, prevProps.state);
    }
    return null;
};

var TrafficControl = function (props) {
    var control = useControl(function () { return new ymaps.control.TrafficControl({
        options: props.options,
        state: props.state
    }); });
    var prevProps = usePrev(props);
    handleEventListener(control, 'showtraffic', props.onShowTraffic, prevProps && prevProps.onShowTraffic);
    handleEventListener(control, 'hidetraffic', props.onHideTraffic, prevProps && prevProps.onHideTraffic);
    if (prevProps) {
        handleData(control.options, props.options, prevProps.options);
        handleData(control.state, props.state, prevProps.state);
    }
    return null;
};

var TypeSelector = function (props) {
    var control = useControl(function () { return new ymaps.control.TypeSelector({
        options: props.options,
        state: props.state
    }); });
    var prevProps = usePrev(props);
    handleEventListener(control, 'expand', props.onExpand, prevProps && prevProps.onExpand);
    handleEventListener(control, 'collapse', props.onCollapse, prevProps && prevProps.onCollapse);
    if (prevProps) {
        handleData(control.options, props.options, prevProps.options);
        handleData(control.state, props.state, prevProps.state);
    }
    return null;
};

var ZoomControl = function (props) {
    var control = useControl(function () { return new ymaps.control.ZoomControl({
        data: props.data,
        options: props.options,
        state: props.state
    }); });
    var prevProps = usePrev(props);
    if (prevProps) {
        handleData(control.data, props.data, prevProps.data);
        handleData(control.options, props.options, prevProps.options);
        handleData(control.state, props.state, prevProps.state);
    }
    return null;
};

var FullscreenControl = function (props) {
    var control = useControl(function () { return new ymaps.control.FullscreenControl({
        data: props.data,
        options: props.options,
        state: props.state
    }); });
    var prevProps = usePrev(props);
    handleEventListener(control, 'fullscreenenter', props.onFullscreenEnter, prevProps && prevProps.onFullscreenEnter);
    handleEventListener(control, 'fullscreenexit', props.onFullscreenExit, prevProps && prevProps.onFullscreenExit);
    if (prevProps) {
        handleData(control.data, props.data, prevProps.data);
        handleData(control.options, props.options, prevProps.options);
        handleData(control.state, props.state, prevProps.state);
    }
    // ymaps api doesn't handle properly selected state change
    var selected = !!(props.state && props.state.selected);
    var prevSelected = !!(prevProps && prevProps.state && prevProps.state.selected);
    if (selected !== prevSelected) {
        if (selected) {
            control.enterFullscreen();
        }
        else {
            control.exitFullscreen();
        }
    }
    return null;
};

var GeolocationControl = function (props) {
    var control = useControl(function () { return new ymaps.control.GeolocationControl({
        data: props.data,
        options: props.options,
        state: props.state
    }); });
    var prevProps = usePrev(props);
    handleEventListener(control, 'locationchange', props.onLocationChange, prevProps && prevProps.onLocationChange);
    if (prevProps) {
        handleData(control.data, props.data, prevProps.data);
        handleData(control.options, props.options, prevProps.options);
        handleData(control.state, props.state, prevProps.state);
    }
    return null;
};

function merge(obj1, obj2) {
    for (var key in obj2) {
        obj1[key] = obj2[key];
    }
    return obj1;
}

function withMap(BaseComponent) {
    return function WithMap(props) {
        return (createElement(MapContext.Consumer, null, function (map) { return createElement(BaseComponent, merge({ map: map }, props)); }));
    };
}

export { Button, Circle, Clusterer, FullscreenControl, GeoObjectCollection, GeolocationControl, HotspotLayer, Layer, _Map as Map, MapEventListener, Placemark, Polygon, Polyline, Rectangle, RulerControl, SearchControl, TrafficControl, TypeSelector, ZoomControl, handleData, handleEventListener, useMap, withMap, withYmaps };
